import React from 'react';
import PropTypes from 'prop-types';

const resetKeysChanged = (prevResetKeys, resetKeys) => {
  if (!prevResetKeys || !resetKeys) return prevResetKeys !== resetKeys;
  if (prevResetKeys.length !== resetKeys.length) return true;
  return prevResetKeys.some((key, index) => key !== resetKeys[index]);
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidUpdate(prevProps) {
    const { hasError } = this.state;
    const { resetKeys } = this.props;
    if (hasError && resetKeysChanged(prevProps.resetKeys, resetKeys)) {
      // eslint-disable-next-line react/no-did-update-set-state -- intentional: retry rendering once the inputs that likely caused the error change
      this.setState({ hasError: false });
    }
  }

  componentDidCatch(error, errorInfo) {
    const { onError } = this.props;
    if (onError) {
      onError(error, errorInfo);
    } else {
      // eslint-disable-next-line no-console -- surface the error somewhere by default instead of failing silently
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }
  }

  render() {
    const { children, fallback } = this.props;
    const { hasError } = this.state;

    if (hasError) return fallback || null;

    return children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  fallback: PropTypes.node,
  onError: PropTypes.func,
  resetKeys: PropTypes.arrayOf(PropTypes.any),
};

ErrorBoundary.defaultProps = {
  fallback: null,
  onError: undefined,
  resetKeys: undefined,
};

export default ErrorBoundary;
